import sanitizeHtml from 'sanitize-html';

// Sellers lay out confirmation pages and emails with their own HTML and CSS, so this is a
// denylist of script and navigation vectors, not a formatting allowlist.
const DENIED_TAGS = new Set([
  'script',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'base',
  'meta',
  'noscript',
  'template',
  'math',
  'portal',
  'fencedframe',
  // SVG animation can rewrite an href to javascript: after sanitizing.
  'set',
  'animate',
  'animatemotion',
  'animatetransform',
]);

const DENIED_ATTRIBUTES = new Set(['srcdoc', 'formaction', 'http-equiv']);

const URL_ATTRIBUTES = [
  'href',
  'src',
  'action',
  'cite',
  'data',
  'poster',
  'background',
  'longdesc',
  'lowsrc',
  'dynsrc',
  'usemap',
  'codebase',
  'classid',
  'archive',
  'manifest',
  'ping',
  'profile',
  'icon',
  'xlink:href',
];

const SAFE_IMAGE_DATA_URL = /^data:image\/(?:png|jpe?g|gif|webp|avif|bmp);/i;
const STYLESHEET_URL = /^https?:\/\//i;
const UNSAFE_CSS = /expression\s*\(|javascript:|vbscript:|-moz-binding|behavior\s*:/i;
const OPENER_SAFE_TARGETS = new Set(['', '_self', '_parent', '_top']);

const stripControlChars = (value: string) => String(value).replace(/[\x00-\x20]+/g, ''); // eslint-disable-line no-control-regex

function isUnsafeCss(css: string) {
  const decoded = css
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\\([0-9a-f]{1,6})\s?/gi, (_, hex: string) => {
      const code = parseInt(hex, 16);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '�';
    })
    .replace(/\\(.)/g, '$1');
  return UNSAFE_CSS.test(decoded);
}

function hasRelToken(rel: string | undefined, token: string) {
  return (rel || '').toLowerCase().split(/\s+/).includes(token);
}

function cleanAttributes(tagName: string, attribs: sanitizeHtml.Attributes) {
  const cleaned: sanitizeHtml.Attributes = {};

  for (const [name, value] of Object.entries(attribs)) {
    if (name.startsWith('on') || DENIED_ATTRIBUTES.has(name)) continue;
    if (name === 'style' && isUnsafeCss(value)) continue;
    if (
      tagName === 'img' &&
      name === 'src' &&
      stripControlChars(value).toLowerCase().startsWith('data:') &&
      !SAFE_IMAGE_DATA_URL.test(stripControlChars(value))
    ) {
      continue;
    }
    cleaned[name] = value;
  }

  if ((tagName === 'a' || tagName === 'area') && cleaned.target !== undefined) {
    if (!OPENER_SAFE_TARGETS.has(cleaned.target.trim().toLowerCase())) {
      const rel = new Set((cleaned.rel || '').split(/\s+/).filter(Boolean));
      rel.add('noopener');
      rel.add('noreferrer');
      cleaned.rel = [...rel].join(' ');
    }
  }

  return { tagName, attribs: cleaned };
}

function isDeniedElement(frame: sanitizeHtml.IFrame) {
  if (DENIED_TAGS.has(frame.tag)) return true;

  // A `<` in a style block is never real CSS, and inside inline SVG the browser
  // parses it as markup the sanitizer never saw.
  if (frame.tag === 'style') return frame.text.includes('<') || isUnsafeCss(frame.text);

  if (frame.tag === 'link') {
    return (
      !hasRelToken(frame.attribs.rel, 'stylesheet') ||
      !STYLESHEET_URL.test(stripControlChars(frame.attribs.href || ''))
    );
  }

  return false;
}

const OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: false,
  allowedAttributes: false,
  allowVulnerableTags: true,
  nonBooleanAttributes: [],
  // Its postcss pass rewrites style attributes and drops any it can't parse in the browser.
  parseStyleAttributes: false,
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  allowedSchemesByTag: { img: ['http', 'https', 'data'] },
  allowedSchemesAppliedToAttributes: URL_ATTRIBUTES,
  allowProtocolRelative: true,
  transformTags: { '*': cleanAttributes },
  exclusiveFilter: isDeniedElement,
};

export function sanitizeSellerHtml(html: string | null | undefined): string {
  if (typeof html !== 'string' || !html) return '';
  return sanitizeHtml(html, OPTIONS);
}
